import { Movie, MovieFilters, MovieStats } from '../types/movie';

export const filterMovies = (movies: Movie[], filters: MovieFilters): Movie[] => {
  return movies.filter(movie => {
    // Search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      const title = (movie.title || '').toLowerCase();
      const director = (movie.director || '').toLowerCase();
      
      if (!title.includes(searchTerm) && !director.includes(searchTerm)) {
        return false;
      }
    }

    // Score above 7 filter
    if (filters.scoreAbove7 && movie.score <= 7) return false;

    // Genre filter
    if (filters.genres.length > 0 && !filters.genres.some(genre => movie.genres.includes(genre))) {
      return false;
    }

    // Director filter
    if (filters.director) {
      const director = (movie.director || '').toLowerCase();
      const filterDirector = filters.director.toLowerCase();
      if (!director.includes(filterDirector)) {
        return false;
      }
    }

    // GF filter
    if (filters.gf && !movie.gf) return false;

    return true;
  });
};

export const sortMovies = (movies: Movie[], sortBy: MovieFilters['sortBy'], sortOrder: 'asc' | 'desc'): Movie[] => {
  return [...movies].sort((a, b) => {
    let aValue: any, bValue: any;

    switch (sortBy) {
      case 'year':
        aValue = a.year;
        bValue = b.year;
        break;
      case 'title':
        aValue = (a.title || '').toLowerCase();
        bValue = (b.title || '').toLowerCase();
        break;
      case 'director':
        aValue = (a.director || '').toLowerCase();
        bValue = (b.director || '').toLowerCase();
        break;
      case 'score':
        aValue = a.score;
        bValue = b.score;
        break;
      case 'lastSeen':
        aValue = a.lastSeen || '';
        bValue = b.lastSeen || '';
        break;
      default:
        return 0;
    }

    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
    } else {
      return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
    }
  });
};

export const calculateStats = (movies: Movie[]): MovieStats => {
  const totalMovies = movies.length;
  const averageScore = totalMovies > 0 ? movies.reduce((sum, movie) => sum + movie.score, 0) / totalMovies : 0;
  const moviesWithGF = movies.filter(movie => movie.gf).length;
  const moviesAbove7 = movies.filter(movie => movie.score > 7).length;

  // Calculate top genres
  const genreCounts: { [key: string]: number } = {};
  movies.forEach(movie => {
    movie.genres.forEach(genre => {
      genreCounts[genre] = (genreCounts[genre] || 0) + 1;
    });
  });
  const topGenres = Object.entries(genreCounts)
    .map(([genre, count]) => ({ genre, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  // Calculate top directors
  const directorCounts: { [key: string]: number } = {};
  movies.forEach(movie => {
    if (movie.director) {
      directorCounts[movie.director] = (directorCounts[movie.director] || 0) + 1;
    }
  });
  const topDirectors = Object.entries(directorCounts)
    .map(([director, count]) => ({ director, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  return {
    totalMovies,
    averageScore: Math.round(averageScore * 10) / 10,
    moviesWithGF,
    moviesAbove7,
    topGenres,
    topDirectors
  };
};

export const getAllGenres = (movies: Movie[]): string[] => {
  const genres = new Set<string>();
  movies.forEach(movie => {
    movie.genres.forEach(genre => genres.add(genre));
  });
  return Array.from(genres).sort();
};

export const getAllDirectors = (movies: Movie[]): string[] => {
  const directors = new Set<string>();
  movies.forEach(movie => {
    if (movie.director) {
      directors.add(movie.director);
    }
  });
  return Array.from(directors).sort();
};
